import { useState } from "react";
import { type CustomerAgeingData, type SmartActionsRecommendation } from "@/types/ageing-analysis";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  User,
} from "lucide-react";
import { CustomerOverviewTab } from "./customer-overview-tab";
import { TransactionsTab } from "./transactions-tab";
import { cn } from "@/lib/utils";
import { type RiskLevelCode, getRiskBadgeClassName } from "@/lib/risk-level";
import { type CurrencyMode } from "@/components/shared/currency-toggle";

interface CustomerDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerAgeingData;
  recommendation: SmartActionsRecommendation;
  onExecuteAction: (actionTitle: string) => void;
  onLogCall: () => void;
  onSendEmail: () => void;
  onViewFullProfile: () => void;
  formatCurrency: (amount: number, currency?: string) => string;
  getPriorityBadgeColor: (priority: string) => string;
  getActionIcon: (actionType: string) => React.ReactNode;
  getActionButtonText: (actionType: string) => string;
  currencyMode?: CurrencyMode;
}

export function CustomerDetailsDrawer({
  open,
  onOpenChange,
  customer,
  recommendation,
  onExecuteAction,
  onLogCall,
  onSendEmail,
  onViewFullProfile,
  formatCurrency,
  getPriorityBadgeColor,
  getActionIcon,
  getActionButtonText,
  currencyMode,
}: Readonly<CustomerDetailsDrawerProps>) {
  const [activeTab, setActiveTab] = useState("overview");

  const getRiskBadgeColor = (risk: string) => getRiskBadgeClassName(risk as RiskLevelCode);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-6xl p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <SheetTitle className="text-xl truncate">{customer.customerName}</SheetTitle>
                <Badge className={cn("capitalize", getRiskBadgeColor(customer.riskLevel))}>
                  {customer.riskLevel}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                <span>Account: {customer.accountNumber}</span>
                <span>•</span>
                <span>{customer.currency}</span>
                <span>•</span>
                <span>{customer.terms}</span>
                {customer.contact && (
                  <>
                    <span>•</span>
                    <span>{customer.contact}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 mr-10">
              <Button
                variant="outline"
                size="sm"
                onClick={onViewFullProfile}
              >
                <User className="h-4 w-4 mr-2" />
                Full Profile
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 mx-6 mt-4 shrink-0">
            <TabsTrigger value="overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="transactions">
              Transactions
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto px-6 pb-6 mt-6">
            <TabsContent value="overview" className="mt-0">
              <CustomerOverviewTab 
                customer={customer} 
                customerId={customer.customerId ? String(customer.customerId) : undefined}
                formatCurrency={formatCurrency}
                currencyMode={currencyMode}
              />
            </TabsContent>

            <TabsContent value="transactions" className="space-y-6 mt-0">
              <TransactionsTab 
                customer={customer}
                customerId={customer.customerId ? String(customer.customerId) : undefined}
                formatCurrency={formatCurrency}
              />
            </TabsContent>
          </div>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
